import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { HiCog, HiChartBar, HiUsers, HiLocationMarker, HiShieldCheck, HiMail } from 'react-icons/hi'
import { CloudRainWind } from 'lucide-react'
import { TextInput } from 'flowbite-react'
import toast from 'react-hot-toast'
import Header from '../components/Header'
import DomainValidationProgress from '../components/DomainValidationProgress'
import OfflineValidationModal from '../components/OfflineValidationModal'
import OtpAccessPanel from '../components/OtpAccessPanel'
import { grantReportAccess, buildReportPageUrl, requestPreviewOtp, isReportReadyStatus, isValidationFailedStatus, isPreviewUnavailableFailure, isPreviewNotReadyMessage, messageForPreviewOtpFailure, DOMAIN_VALIDATION_FAILURE_MESSAGE, saveLeadSession, getLeadSession, clearLeadSession, submitLead, fetchLeadStatus, firstLeadReference } from '../constants/inviteCode'
import { fireConfetti } from '../utils/confetti'
import { getLeadSignupErrorMessage } from '../utils/apiError'

const extractDomain = (email) => {
  if (!email || !email.includes('@')) return ''
  const parts = email.split('@')[1].split('.')
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`
  }
  return email.split('@')[1]
}

const CityMarketing = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [showValidation, setShowValidation] = useState(false)
  const [domain, setDomain] = useState('')
  const [leadSubmitted, setLeadSubmitted] = useState(false)
  const [isSubmittingLead, setIsSubmittingLead] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState(null)
  
  // Enhanced validation states
  const [validationJobId, setValidationJobId] = useState(null)
  const [leadId, setLeadId] = useState(null)
  const [showValidationProgress, setShowValidationProgress] = useState(false)
  const [showOtpPrompt, setShowOtpPrompt] = useState(false)
  const [otpPendingMessage, setOtpPendingMessage] = useState('')
  const [validationFailed, setValidationFailed] = useState(false)
  const [showOfflineModal, setShowOfflineModal] = useState(false)
  const [, setValidationResult] = useState(null)
  const [serverConfirmedEmail, setServerConfirmedEmail] = useState('')
  const [validationRejectedEmail, setValidationRejectedEmail] = useState('')
  const [restoringSession, setRestoringSession] = useState(true)

  const persistLeadSession = (overrides = {}) => {
    const pick = (key, fallback) =>
      Object.prototype.hasOwnProperty.call(overrides, key) ? overrides[key] : fallback

    saveLeadSession({
      email: pick('email', email),
      leadId: pick('leadId', leadId),
      jobId: pick('jobId', validationJobId),
      domain: pick('domain', domain),
      validationFailed: pick('validationFailed', validationFailed),
      failureMessage: pick(
        'failureMessage',
        validationFailed ? otpPendingMessage : null,
      ),
    })
  }

  const resetLeadFlow = ({ keepEmail = '', rejectEmail = false } = {}) => {
    clearLeadSession()
    setShowValidation(false)
    setDomain('')
    setLeadSubmitted(false)
    setValidationJobId(null)
    setLeadId(null)
    setShowValidationProgress(false)
    setShowOtpPrompt(false)
    setOtpPendingMessage('')
    setValidationFailed(false)
    setServerConfirmedEmail('')
    setValidationRejectedEmail(rejectEmail && keepEmail ? keepEmail : '')
    if (keepEmail) {
      setEmail(keepEmail)
    }
  }

  const applyValidationFailed = ({
    emailValue,
    domainValue,
    leadIdValue = null,
    jobIdValue = null,
    message = DOMAIN_VALIDATION_FAILURE_MESSAGE,
  }) => {
    const failureMessage = message || DOMAIN_VALIDATION_FAILURE_MESSAGE
    setLeadSubmitted(true)
    setServerConfirmedEmail(emailValue)
    setValidationRejectedEmail('')
    if (domainValue) setDomain(domainValue)
    if (leadIdValue) setLeadId(String(leadIdValue))
    if (jobIdValue) {
      setValidationJobId(jobIdValue)
      setShowValidationProgress(true)
    } else {
      setValidationJobId(null)
      setShowValidationProgress(false)
    }
    setShowOtpPrompt(false)
    setValidationFailed(true)
    setOtpPendingMessage(failureMessage)
    setShowValidation(true)
    persistLeadSession({
      email: emailValue,
      leadId: leadIdValue,
      jobId: jobIdValue,
      domain: domainValue,
      validationFailed: true,
      failureMessage,
    })
  }

  const applyCreatedLeadResponse = async (emailValue, data) => {
    setLeadSubmitted(true)
    setServerConfirmedEmail(emailValue)
    setValidationRejectedEmail('')
    setValidationFailed(false)
    if (data.leadId) setLeadId(String(data.leadId))
    if (data.domain) setDomain(data.domain)

    if (isReportReadyStatus(data.status)) {
      try {
        await requestPreviewOtp(emailValue)
        setShowOtpPrompt(true)
        setShowValidationProgress(false)
        setOtpPendingMessage('')
        persistLeadSession({
          email: emailValue,
          leadId: data.leadId,
          jobId: null,
          domain: data.domain,
          validationFailed: false,
          failureMessage: null,
        })
      } catch (otpError) {
        console.error('Could not send preview OTP:', otpError)
        const message = messageForPreviewOtpFailure(otpError.message)
        if (isPreviewUnavailableFailure(otpError.message)) {
          applyValidationFailed({
            emailValue,
            domainValue: data.domain || extractDomain(emailValue),
            leadIdValue: data.leadId,
            message,
          })
          return
        }
        setValidationFailed(false)
        setOtpPendingMessage(message)
        setShowOtpPrompt(false)
      }
      return
    }

    if (data.jobId) {
      let statusData = data
      try {
        statusData = await fetchLeadStatus(data.jobId)
        if (isValidationFailedStatus(statusData.status)) {
          applyValidationFailed({
            emailValue,
            domainValue: data.domain || statusData.domain || extractDomain(emailValue),
            leadIdValue: data.leadId ?? statusData.leadId,
            jobIdValue: data.jobId,
            message:
              statusData.errorMessage ||
              statusData.error_message ||
              DOMAIN_VALIDATION_FAILURE_MESSAGE,
          })
          return
        }
        if (isReportReadyStatus(statusData.status) || Number(statusData.progress) >= 100) {
          await applyExistingLeadResponse(emailValue, {
            ...statusData,
            leadId: data.leadId ?? statusData.leadId,
            domain: data.domain ?? statusData.domain,
          })
          return
        }
      } catch (statusError) {
        console.error('Could not verify validation job status:', statusError)
      }

      setValidationJobId(data.jobId)
      setShowValidationProgress(true)
      setShowOtpPrompt(false)
      persistLeadSession({
        email: emailValue,
        leadId: data.leadId ?? statusData.leadId,
        jobId: data.jobId,
        domain: data.domain,
        validationFailed: false,
        failureMessage: null,
      })
      return
    }

    console.log('No validation job ID in response:', data)
  }

  const applyExistingLeadResponse = async (emailValue, data = {}) => {
    if (isValidationFailedStatus(data?.status)) {
      applyValidationFailed({
        emailValue,
        domainValue: data.domain || extractDomain(emailValue),
        leadIdValue: data.leadId,
        jobIdValue: data.jobId,
        message:
          data.errorMessage ||
          data.error_message ||
          DOMAIN_VALIDATION_FAILURE_MESSAGE,
      })
      return
    }

    if (data?.jobId && !isReportReadyStatus(data?.status)) {
      await applyCreatedLeadResponse(emailValue, data)
      return
    }

    const resolvedLeadId = firstLeadReference(data?.leadId, getLeadSession()?.leadId)
    const resolvedDomain = data?.domain || extractDomain(emailValue)

    setLeadSubmitted(true)
    setServerConfirmedEmail(emailValue)
    setValidationRejectedEmail('')
    setValidationFailed(false)
    if (resolvedLeadId) setLeadId(resolvedLeadId)
    setDomain(resolvedDomain)
    setShowValidationProgress(false)

    try {
      const otpData = await requestPreviewOtp(emailValue)
      const otpDomain = otpData.domain || resolvedDomain
      const otpLeadId = firstLeadReference(otpData.leadId, resolvedLeadId)
      if (otpLeadId && otpLeadId !== resolvedLeadId) {
        setLeadId(otpLeadId)
      }
      if (otpDomain) setDomain(otpDomain)
      setShowOtpPrompt(true)
      setOtpPendingMessage('')
      fireConfetti({ allowRepeat: true })
      persistLeadSession({
        email: emailValue,
        leadId: otpLeadId,
        jobId: null,
        domain: otpDomain,
        validationFailed: false,
        failureMessage: null,
      })
    } catch (otpError) {
      console.error('Preview OTP unavailable:', otpError)
      const message = messageForPreviewOtpFailure(otpError.message)
      if (isPreviewUnavailableFailure(otpError.message)) {
        applyValidationFailed({
          emailValue,
          domainValue: resolvedDomain,
          leadIdValue: resolvedLeadId,
          message,
        })
        return
      }
      setShowOtpPrompt(false)
      setValidationFailed(false)
      setOtpPendingMessage(message)
      persistLeadSession({
        email: emailValue,
        leadId: resolvedLeadId,
        jobId: null,
        domain: resolvedDomain,
        validationFailed: false,
        failureMessage: null,
      })
    }
  }

  const showLeadSignupError = (message) => {
    toast.error(message, { duration: 12000 })
  }

  const handleLeadSignupFailure = (emailValue, { fromSession = false, status, data, error } = {}) => {
    const message = getLeadSignupErrorMessage({ status, data, error })
    const isDomainRejection = status === 400

    if (isDomainRejection) {
      setValidationRejectedEmail(emailValue)
    }

    if (fromSession) {
      resetLeadFlow({
        keepEmail: emailValue,
        rejectEmail: isDomainRejection,
      })
    } else {
      setShowValidation(false)
      setDomain('')
    }

    showLeadSignupError(message)
  }

  const syncLeadWithServer = async (emailValue, { fromSession = false } = {}) => {
    if (!emailValue) return false

    setIsSubmittingLead(true)

    try {
      const { ok, status, data } = await submitLead(emailValue)

      if (ok) {
        setShowValidation(true)
        await applyCreatedLeadResponse(emailValue, data)
        return true
      }

      if (status === 409) {
        setShowValidation(true)
        await applyExistingLeadResponse(emailValue, data)
        return true
      }

      console.error('Failed to save lead:', status, data)
      handleLeadSignupFailure(emailValue, { fromSession, status, data })
      return false
    } catch (error) {
      console.error('Error saving lead:', error)
      handleLeadSignupFailure(emailValue, { fromSession, error })
      return false
    } finally {
      setIsSubmittingLead(false)
    }
  }

  const resumeLeadSession = async (session) => {
    const sessionEmail = String(session?.email ?? '').trim()
    if (!sessionEmail) {
      clearLeadSession()
      return
    }

    setEmail(sessionEmail)

    if (session.validationFailed) {
      applyValidationFailed({
        emailValue: sessionEmail,
        domainValue: session.domain || extractDomain(sessionEmail),
        leadIdValue: session.leadId,
        jobIdValue: session.jobId,
        message: session.failureMessage || DOMAIN_VALIDATION_FAILURE_MESSAGE,
      })
      return
    }

    if (session.jobId) {
      try {
        const statusData = await fetchLeadStatus(session.jobId)
        if (isValidationFailedStatus(statusData.status)) {
          applyValidationFailed({
            emailValue: sessionEmail,
            domainValue: statusData.domain || session.domain || extractDomain(sessionEmail),
            leadIdValue: session.leadId ?? statusData.leadId,
            jobIdValue: session.jobId,
            message:
              statusData.errorMessage ||
              statusData.error_message ||
              DOMAIN_VALIDATION_FAILURE_MESSAGE,
          })
          return
        }
        if (isReportReadyStatus(statusData.status) || Number(statusData.progress) >= 100) {
          setShowValidation(true)
          await applyExistingLeadResponse(sessionEmail, {
            ...statusData,
            leadId: session.leadId ?? statusData.leadId,
          })
          return
        }

        setShowValidation(true)
        setDomain(statusData.domain || session.domain || extractDomain(sessionEmail))
        if (session.leadId) setLeadId(String(session.leadId))
        setLeadSubmitted(true)
        setServerConfirmedEmail(sessionEmail)
        setValidationJobId(session.jobId)
        setShowValidationProgress(true)
        return
      } catch (statusError) {
        console.error('Could not restore validation job from session:', statusError)
      }
    }

    await syncLeadWithServer(sessionEmail, { fromSession: true })
  }

  useEffect(() => {
    const session = getLeadSession()
    if (session?.email) {
      resumeLeadSession(session).finally(() => setRestoringSession(false))
    } else {
      setRestoringSession(false)
    }
    // Mount-only: restores a persisted lead session exactly once. Re-running
    // would re-resume the session and clobber in-progress input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveLead = async (emailValue) => {
    if (
      isSubmittingLead ||
      serverConfirmedEmail === emailValue ||
      validationRejectedEmail === emailValue
    ) {
      return
    }
    await syncLeadWithServer(emailValue)
  }

  const debouncedValidateAndSave = (emailValue) => {
    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    // Set new timer for 1.5 seconds
    const newTimer = setTimeout(() => {
      if (emailValue && emailValue.includes('@') && emailValue.includes('.')) {
        const extractedDomain = extractDomain(emailValue)
        // Check if domain has at least 2 parts and ends with a valid TLD (2+ characters)
        const domainParts = extractedDomain.split('.')
        const hasValidTLD = domainParts.length >= 2 && domainParts[domainParts.length - 1].length >= 2
        
        if (hasValidTLD) {
          setDomain(extractedDomain)
          setShowValidation(true)
          
          // Save lead when validation passes (once per distinct email)
          if (
            !isSubmittingLead &&
            serverConfirmedEmail !== emailValue &&
            validationRejectedEmail !== emailValue
          ) {
            saveLead(emailValue)
          }
        } else {
          setShowValidation(false)
          setDomain('')
        }
      } else {
        setShowValidation(false)
        setDomain('')
      }
    }, 1500) // 1.5 second delay

    setDebounceTimer(newTimer)
  }

  useEffect(() => {
    if (restoringSession) return undefined

    debouncedValidateAndSave(email)

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
    // Keyed on `email` only, by necessity: this effect schedules the debounce
    // timer via debouncedValidateAndSave, which calls setDebounceTimer. Listing
    // debounceTimer would therefore retrigger the effect it just scheduled, in
    // a loop. debouncedValidateAndSave is likewise recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  }, [debounceTimer])

  // Handle validation completion
  const handleValidationComplete = (result) => {
    setValidationResult(result)
    setValidationFailed(false)
    setOtpPendingMessage('')
    // Keep progress visible in-session, but persist completion so refresh uses OTP flow.
    // Explicitly clear failure flags so a prior attempt cannot revive on refresh.
    persistLeadSession({
      email,
      leadId: leadId ?? result?.leadId,
      jobId: null,
      domain,
      validationFailed: false,
      failureMessage: null,
    })
    console.log('Validation completed:', result)
  }

  // Handle validation error — keep progress UI visible so the failed step is clear.
  const handleValidationError = (error) => {
    console.error('Validation error:', error)
    const failureMessage =
      (typeof error === 'string' && error.trim()) || DOMAIN_VALIDATION_FAILURE_MESSAGE
    applyValidationFailed({
      emailValue: email,
      domainValue: domain || extractDomain(email),
      leadIdValue: leadId,
      jobIdValue: validationJobId,
      message: failureMessage,
    })
  }

  // Handle offline mode
  const handleOfflineMode = () => {
    setShowValidationProgress(false)
    setShowOfflineModal(true)
  }

  // Handle resuming validation from offline modal
  const handleResumeValidation = (result) => {
    setShowOfflineModal(false)
    if (result) {
      handleValidationComplete(result)
    } else {
      setShowValidationProgress(true)
    }
  }

  const handleInviteCodeVerified = (report) => {
    const leadEmail = report.email || serverConfirmedEmail || email
    const reportLeadId = firstLeadReference(
      report.leadId,
      leadId,
      validationJobId,
      getLeadSession()?.leadId,
    )
    const reportDomain = report.domain || domain
    const otp = report.otp

    if (!reportDomain || !otp || !leadEmail) {
      toast.error(
        'Unable to open your report after verification. Please request a new access code and try again.',
      )
      return
    }

    if (!reportLeadId) {
      toast.error(
        'Unable to open your report: missing lead ID. Please refresh and try again.',
      )
      return
    }

    clearLeadSession()
    grantReportAccess(reportLeadId, {
      domain: reportDomain,
      lead: reportLeadId,
      otp,
      email: leadEmail,
    })
    navigate(buildReportPageUrl(reportDomain, reportLeadId, otp, leadEmail), {
      state: { domain: reportDomain, otp, email: leadEmail },
    })
  }

  const handleOtpVerified = ({ otp, domain: reportDomain, email: leadEmail }) => {
    handleInviteCodeVerified({
      domain: reportDomain,
      otp,
      email: leadEmail || serverConfirmedEmail || email,
      leadId,
    })
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 py-20 pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Every Snap Improves Your City's Image
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-4xl mx-auto">
            Join the growing network of cities using CitySeeCityFix to streamline issue reporting, 
            improve response times, and build stronger community relationships.
          </p>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-4xl mx-auto">
            If you are a resident of the City, you do everything through the app on your phone <a href="#" className="text-primary-600 hover:text-primary-700 font-medium">get the app</a>. If you work for the City and want more information or enable a free trial period, enter your official city email below for verification.
          </p>
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md">
              <TextInput
                type="email"
                icon={HiMail}
                placeholder="Enter your city email address to verify City Availability"
                value={email}
                onChange={(e) => {
                  const nextEmail = e.target.value
                  setEmail(nextEmail)
                  if (validationRejectedEmail && nextEmail !== validationRejectedEmail) {
                    setValidationRejectedEmail('')
                  }
                  if (serverConfirmedEmail && nextEmail !== serverConfirmedEmail) {
                    resetLeadFlow({ keepEmail: nextEmail })
                  }
                }}
                required
              />
            </div>
            
            {restoringSession ? (
              <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 w-full max-w-md text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Checking your report status…</p>
              </div>
            ) : showValidation && domain && !showValidationProgress && !showOtpPrompt && (
              <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 w-full max-w-md">
                <div className="text-center">
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    Checking {domain}
                    <span className="inline-block w-6 text-left">
                      <span className="animate-pulse">...</span>
                    </span>
                  </p>
                  <div className="flex items-center justify-center space-x-2">
                    {isSubmittingLead ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          Saving your information...
                        </span>
                      </>
                    ) : leadSubmitted ? (
                      <>
                        <span className="text-green-500 text-xl">✅</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          Information saved successfully!
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-green-500 text-xl">✅</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          Test passed
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Validation Progress */}
            {showValidationProgress && validationJobId && (
              <div className="mt-6">
                <DomainValidationProgress
                  jobId={validationJobId}
                  leadId={leadId}
                  domain={domain}
                  email={email}
                  onComplete={handleValidationComplete}
                  onError={handleValidationError}
                  onOfflineMode={handleOfflineMode}
                  onInviteCodeVerified={handleInviteCodeVerified}
                />
              </div>
            )}

            {showOtpPrompt && leadSubmitted && domain && email && (
              <div className="mt-6 p-5 bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 w-full max-w-md">
                <OtpAccessPanel
                  email={email}
                  domain={domain}
                  onVerified={handleOtpVerified}
                />
              </div>
            )}

            {leadSubmitted && !validationJobId && !showOtpPrompt && (
              <div
                className={`mt-4 p-4 rounded-lg border w-full max-w-md ${
                  validationFailed
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                }`}
              >
                <div className="text-center">
                  <p
                    className={`font-medium mb-2 ${
                      validationFailed
                        ? 'text-red-800 dark:text-red-200'
                        : 'text-blue-800 dark:text-blue-200'
                    }`}
                  >
                    {validationFailed
                      ? 'Domain validation failed'
                      : otpPendingMessage
                        ? (isPreviewNotReadyMessage(otpPendingMessage)
                          ? 'Report not ready yet'
                          : 'Unable to open report')
                        : 'Preparing your report'}
                  </p>
                  <p
                    className={`text-sm ${
                      validationFailed
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-blue-600 dark:text-blue-300'
                    }`}
                  >
                    {otpPendingMessage ||
                      'Your city discovery report is being prepared. Watch for an email from us with your access code.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Cities Choose CitySeeCityFix
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              A friendly data collection solution to front end your municipal operations
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Smart Routing & Prioritization
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  CitySeeCityFix automatically builds a live directory of public-facing departments and 
                  intelligently routes each submission to the right team. Issues are categorized 
                  and prioritized based on safety, accessibility, and repair scale—ensuring 
                  critical problems get immediate attention.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Seamless System Integration
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Plug CitySeeCityFix into your existing ticketing or work-order systems through our 
                  robust API. Submissions flow directly into your current processes with zero 
                  changes required on your side. Maintain your existing workflows while gaining 
                  new capabilities.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Custom Branding & Control
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Participating cities get full admin access with comprehensive control over multi-point issue routing! Align CitySeeCityFix with your city's identity with control over logo choice and publicly exposed contacts and emails.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  GIS-Powered Intelligence
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Your teams get the clearest picture possible. Every report is enriched with precise ESRI-compatible location data, constituent information, explicit contextual details, and of course a picture is worth a thousand words! No more guesswork what to put on the truck.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Community Engagement & Trust
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  CitySeeCityFix reframes complaints as collaboration opportunities. Residents see city 
                  staff as partners rather than impersonal bureaucracy, building trust, pride, 
                  and stronger community ties that benefit everyone.
                </p>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Powerful Features for Modern Cities
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HiCog className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Smart Automation
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                AI-powered issue detection and automatic categorization
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HiChartBar className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Real-time Analytics
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Live dashboards and comprehensive reporting
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HiUsers className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Team Management
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Role-based access control and user management
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HiLocationMarker className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                GIS Integration
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Precise location mapping and spatial analysis
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CloudRainWind className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Crowd-Sourced Disaster Response
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Instant notifications and rapid issue routing
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HiShieldCheck className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Secure & Compliant
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Enterprise-grade security and data protection
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HiCog className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Custom Workflows
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Tailored processes for your city's needs
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HiUsers className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Community Portal
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Resident engagement and progress tracking
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Simple Implementation Process
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Get up and running in weeks, not months
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-primary-600 dark:text-primary-400">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Assessment & Planning
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We'll work with your team to understand your current processes and design 
                the optimal implementation strategy.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-primary-600 dark:text-primary-400">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Setup & Integration
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Our team handles the technical setup, API integration, and custom branding 
                to match your city's identity.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-primary-600 dark:text-primary-400">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Launch & Training
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Go live with comprehensive staff training and ongoing support to ensure 
                smooth operations from day one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your City?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join the growing network of cities using CitySeeCityFix to build better communities 
            through smarter infrastructure management.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="inline-block bg-white text-primary-600 font-semibold py-4 px-8 rounded-lg text-lg hover:bg-gray-100 transition-colors duration-200"
            >
              Start Your Free Trial
            </Link>
            <a
              href="mailto:sales@CitySeeCityFix.com"
              className="inline-block border-2 border-white text-white font-semibold py-4 px-8 rounded-lg text-lg hover:bg-white hover:text-primary-600 transition-colors duration-200"
            >
              Schedule a Demo
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 dark:bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  className="h-8 w-8 rounded-full"
                  src="/maggie_round.png"
                  alt="CitySeeCityFix"
                />
                <span className="ml-3 text-xl font-bold text-white">
                  CitySeeCityFix
                </span>
              </div>
              <p className="text-gray-400">
                The easiest way to report city issues—just one click away.
              </p>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">For Residents</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white">Home</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/maggie" className="hover:text-white">Meet Maggie</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">For Cities</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white">Learn More</Link></li>
                <li><Link to="/login" className="hover:text-white">Admin Login</Link></li>
                <li><a href="https://api.cityseecityfix.com" className="hover:text-white">API Docs</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="mailto:sales@CitySeeCityFix.com" className="hover:text-white">Sales</a></li>
                <li><a href="mailto:support@CitySeeCityFix.com" className="hover:text-white">Support</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 CitySeeCityFix. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Offline Validation Modal */}
      <OfflineValidationModal
        isOpen={showOfflineModal}
        onClose={() => setShowOfflineModal(false)}
        email={email}
        domain={domain}
        jobId={validationJobId}
        onResumeValidation={handleResumeValidation}
      />
    </div>
  )
}

export default CityMarketing